import { Dialog, Text, SpinnerWheel, Layout } from '@loadsmart/miranda-react'
import { useState, useRef, createContext, useContext } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { observationClient } from '../clients/ObservationClient'
import { GateFeedFilter } from '../clients/type'
import { selectUserToken } from '../store/user'
import { ApiError } from '../util/errors/ApiError'
import { BaseError } from '../util/errors/BaseError'
import { useNotification } from './useNotification'
import { NotificationDownloadBody } from '../components/NotificationDownloadBody'
import { useMessage } from './useMessage'
import { downloadCSV } from '../util/file'

type ContextState = {
  isLoading: boolean
  error: Error | null
  data: string | null
  contentLength: number | null
  exportData: (params: {
    filters: GateFeedFilter[]
    report: boolean
    inventoryOnly?: boolean
    includeVehicles?: boolean
    exportDate?: string
    exportFormat?: string
    timezone?: string
  }) => void
}

const Context = createContext<ContextState>({
  isLoading: false,
  error: null,
  data: null,
  contentLength: null,
  exportData: () => {},
})

export const ExportProvider = ({ children }: { children: JSX.Element }): JSX.Element => {
  const messenger = useMessage()
  const {
    Component: Notification,
    trigger: triggerNotification,
    isVisible: isDownloading,
  } = useNotification({
    durationMs: 'indefinite',
  })

  const cancelRef = useRef<(() => void) | null>(null)
  const token = useSelector(selectUserToken, shallowEqual)
  const [error, setError] = useState<null | Error>(null)
  const [data, setData] = useState<string | null>(null)
  const [contentLength, setContentLength] = useState<number | null>(null)

  const handleExportRequest = async (requestParams: {
    filters: GateFeedFilter[]
    report: boolean
    inventoryOnly?: boolean
    includeVehicles?: boolean
    exportDate?: string
    exportFormat?: string
    timezone?: string
  }): Promise<void> => {
    if (!token) {
      throw new ApiError({ type: 'Unauthorized' })
    }

    if (isDownloading) {
      const nextErr = new BaseError({
        cause: new Error('Export already in progress'),
      })
      setError(nextErr)
      messenger.error(nextErr)
      return
    }

    triggerNotification({ isVisible: true })

    try {
      cancelRef.current = await observationClient.readObservationCSV({
        token,
        ...requestParams,
        onComplete({ data: nextData, fileName, contentLength }) {
          setData(nextData)
          setContentLength(contentLength)
          setError(null)
          triggerNotification({ isVisible: false })
          cancelRef.current = null

          if (nextData) {
            downloadCSV(nextData, fileName)
            messenger.success('Report generated')
          } else {
            messenger.danger('No data available for the selected filters')
          }
        },
        onError(nextError) {
          messenger.error(nextError)
          triggerNotification({ isVisible: false })
          setData(null)
          setError(nextError)
          cancelRef.current?.()
        },
      })
    } catch (e) {
      if (e instanceof Error) {
        messenger.error(e)
        cancelRef.current?.()
        triggerNotification({ isVisible: false })
        setError(e)
        setData(null)
      }
    }
  }

  const CurrentNotification = (
    <Dialog open={isDownloading} size="small">
      <Dialog.Body>
        <Layout.Stack align="center" justify="center">
          <SpinnerWheel size="medium" />
          <Text variant="body-md-bold">Generating report</Text>
          <Text variant="body-sm">This might take a couple of minutes, thanks for waiting.</Text>
        </Layout.Stack>
      </Dialog.Body>
    </Dialog>
  )

  return (
    <Context.Provider
      value={{
        isLoading: isDownloading,
        error,
        data,
        contentLength: contentLength,
        exportData: (params) => {
          handleExportRequest(params)
        },
      }}
    >
      <>
        {CurrentNotification}
        {children}
      </>
    </Context.Provider>
  )
}

export const useObservationExports = (): ContextState => {
  const { isLoading, error, data, contentLength, exportData } = useContext(Context)

  return {
    exportData,
    isLoading,
    error,
    data,
    contentLength,
  }
}
